import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import SiteFooter from '@/components/SiteFooter';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { Lock, ArrowLeft, MessageCircle, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const formatPrice = (price, currency) => {
  if (!price) return 'Price on Application';
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${sym}${Number(price).toLocaleString()}`;
};

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [enquiring, setEnquiring] = useState(false);
  const isMember = user?.role === 'member' || user?.role === 'admin';

  useEffect(() => {
    api.get(`/listings/${id}`)
      .then(r => setListing(r.data))
      .catch(() => navigate('/browse'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const startEnquiry = async () => {
    if (!isMember) { navigate(user ? '/membership/apply' : '/register'); return; }
    setEnquiring(true);
    try {
      const r = await api.post('/conversations', {
        listing_id: id,
        initial_message: `Hi, I'm interested in "${listing.title}". Could you share more details?`,
      });
      navigate(`/dashboard/enquiries?conv=${r.data.conversation_id}`);
    } catch (e) {
      if (e.response?.status === 400 && e.response?.data?.detail?.includes('already')) {
        try {
          const convs = await api.get('/conversations');
          const existing = convs.data.find(c => c.listing_id === id);
          if (existing) navigate(`/dashboard/enquiries?conv=${existing.id}`);
          else toast.error('Please visit your enquiries page');
        } catch { toast.error('Failed to open conversation'); }
      } else {
        toast.error(e.response?.data?.detail || 'Failed to start enquiry');
      }
    } finally { setEnquiring(false); }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#fff', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ backgroundColor: '#0A0A0A', height: '120px' }} />
        <div className="container-app py-10">
          <div className="grid lg:grid-cols-2 gap-14">
            <div style={{ height: '560px', backgroundColor: '#F5F5F5' }} />
            <div className="space-y-4 pt-4">
              {[60, 80, 40, 100, 100, 60].map((w, i) => (
                <div key={i} style={{ height: i === 0 ? '11px' : i === 1 ? '36px' : i === 2 ? '28px' : '14px', backgroundColor: '#F0F0F0', width: `${w}%` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const images = listing.images?.length > 0 ? listing.images.map(getImageUrl) : [PLACEHOLDER_IMAGE];
  const detailFields = [
    ['Medium', listing.medium],
    ['Year', listing.year],
    ['Dimensions', listing.dimensions],
    ['Condition', listing.condition],
  ].filter(([, v]) => v);

  return (
    <div style={{ backgroundColor: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

      {/* Breadcrumb strip */}
      <div style={{ borderBottom: '1px solid #E5E5E5', backgroundColor: '#FAFAFA' }}>
        <div className="container-app py-3">
          <Link to="/browse" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider transition-opacity hover:opacity-50" style={{ color: '#666' }}>
            <ArrowLeft size={13} /> Back to Browse
          </Link>
        </div>
      </div>

      <div className="container-app py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">

          {/* ── Image Viewer ── */}
          <div>
            <div className="relative overflow-hidden" style={{ aspectRatio: '4/5', backgroundColor: '#F5F5F5' }} data-testid="listing-detail-hero-image">
              <img src={images[imgIdx]} alt={listing.title} className="w-full h-full object-cover" onError={e => { e.target.src = PLACEHOLDER_IMAGE; }} />
              {listing.featured && (
                <span className="absolute top-4 left-4 text-[10px] px-3 py-1.5 uppercase tracking-[0.15em] font-semibold" style={{ backgroundColor: '#000', color: '#fff' }}>
                  Featured
                </span>
              )}
              {listing.status === 'sold' && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(10,10,10,0.6)' }}>
                  <span className="text-white text-2xl font-bold uppercase tracking-[0.3em]">Sold</span>
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white transition-colors hover:bg-black hover:text-white"
                    style={{ border: '1px solid rgba(0,0,0,0.15)' }} aria-label="Previous image">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={() => setImgIdx(i => (i + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white transition-colors hover:bg-black hover:text-white"
                    style={{ border: '1px solid rgba(0,0,0,0.15)' }} aria-label="Next image">
                    <ChevronRight size={16} />
                  </button>
                  {/* Slide counter */}
                  <span className="absolute bottom-4 right-4 text-xs tabular-nums text-white/70">
                    {String(imgIdx + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
                  </span>
                </>
              )}
            </div>
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setImgIdx(i)}
                    className="w-14 h-14 overflow-hidden flex-shrink-0 transition-all"
                    style={{ border: `1px solid ${i === imgIdx ? '#000' : '#E5E5E5'}` }}>
                    <img src={img} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = PLACEHOLDER_IMAGE; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Listing Info ── */}
          <div>
            {/* Category + type */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: '#999' }}>{listing.category_name || 'Artwork'}</span>
              {listing.type_name && (
                <>
                  <span style={{ color: '#DDD' }}>·</span>
                  <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: '#999' }}>{listing.type_name}</span>
                </>
              )}
            </div>

            {/* Title */}
            <h1 className="font-bold mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', lineHeight: 1.05, letterSpacing: '-0.025em' }}>
              {listing.title}
            </h1>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6 pb-6" style={{ borderBottom: '1px solid #E5E5E5' }}>
              <span className="text-3xl font-bold tabular-nums">{formatPrice(listing.price, listing.currency)}</span>
              {listing.status === 'sold' && (
                <span className="text-xs uppercase tracking-[0.15em] px-2.5 py-1 font-semibold" style={{ backgroundColor: '#F5F5F5', color: '#666' }}>Sold</span>
              )}
            </div>

            {/* Description */}
            {listing.description && (
              <p className="text-sm leading-relaxed mb-7" style={{ color: '#555' }}>{listing.description}</p>
            )}

            {/* Detail grid */}
            {detailFields.length > 0 && (
              <div className="grid grid-cols-2 mb-7" style={{ border: '1px solid #E5E5E5' }}>
                {detailFields.map(([k, v], i) => (
                  <div key={k} className="p-4" style={{
                    borderRight: i % 2 === 0 ? '1px solid #E5E5E5' : 'none',
                    borderBottom: i < detailFields.length - 2 ? '1px solid #E5E5E5' : 'none',
                  }}>
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: '#999' }}>{k}</p>
                    <p className="text-sm font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Provenance */}
            {listing.provenance && (
              <div className="mb-7 p-4" style={{ borderLeft: '2px solid #000', backgroundColor: '#FAFAFA' }}>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: '#999' }}>Provenance</p>
                <p className="text-sm leading-relaxed">{listing.provenance}</p>
              </div>
            )}

            {/* Seller section */}
            <div style={{ border: '1px solid #E5E5E5' }} data-testid="listing-detail-seller-section">
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E5E5', backgroundColor: '#FAFAFA' }}>
                <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#999' }}>Seller Information</p>
              </div>
              {isMember ? (
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center text-sm font-bold flex-shrink-0 text-white"
                      style={{ backgroundColor: '#000' }}>
                      {initials(listing.seller?.name)}
                    </span>
                    <div>
                      <p className="text-sm font-bold">{listing.seller?.name || 'Private Seller'}</p>
                      {listing.seller?.profile_type && (
                        <p className="text-[11px] uppercase tracking-[0.12em]" style={{ color: '#888' }}>{listing.seller.profile_type}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {listing.seller?.email && (
                      <p className="text-xs" style={{ color: '#666' }}>
                        Email: <a href={`mailto:${listing.seller.email}`} className="font-semibold underline underline-offset-2"
                          style={{ color: '#000' }}>{listing.seller.email}</a>
                      </p>
                    )}
                    {listing.seller?.phone && <p className="text-xs" style={{ color: '#666' }}>Phone: {listing.seller.phone}</p>}
                    {listing.seller?.location && <p className="text-xs" style={{ color: '#666' }}>Location: {listing.seller.location}</p>}
                  </div>
                  {user && listing.seller?.id !== user.id && listing.status === 'active' && (
                    <div className="mt-5 pt-5" style={{ borderTop: '1px solid #E5E5E5' }}>
                      <button onClick={startEnquiry} disabled={enquiring}
                        className="w-full flex items-center justify-center gap-2 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-200 disabled:opacity-60"
                        style={{ backgroundColor: '#000', color: '#fff' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
                        data-testid="listing-enquire-button">
                        <MessageCircle size={15} />
                        {enquiring ? 'Opening chat…' : 'Enquire about this listing'}
                      </button>
                      <p className="text-[11px] text-center mt-2.5" style={{ color: '#999' }}>Opens a private chat with the seller</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4" style={{ border: '1px solid #E5E5E5' }}>
                    <Lock size={18} style={{ color: '#999' }} />
                  </div>
                  <p className="text-sm font-bold mb-1.5">Members Only</p>
                  <p className="text-xs mb-6" style={{ color: '#666', maxWidth: '280px', margin: '0 auto 1.5rem' }}>
                    Join The Listing Room to view seller details and make direct enquiries.
                  </p>
                  <button onClick={() => navigate(user ? '/membership/apply' : '/register')}
                    data-testid="listing-detail-request-access-button"
                    className="inline-flex items-center gap-2 px-7 py-3 text-xs font-semibold uppercase tracking-wider transition-colors"
                    style={{ backgroundColor: '#000', color: '#fff' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                    {user ? 'Apply for Membership' : 'Apply to Join'} <ArrowRight size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
